import React, { useEffect, useState } from 'react';
import { apiRequest } from '../auth/apiClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ENHANCEMENT 3 — Free-trial progress banner.
 *
 * Renders directly below the SetupChecklistBanner on the dashboard for any
 * agent whose `subscription_status === 'trialing'`. Hidden for active /
 * past_due / cancelled subscriptions.
 *
 * Colour shifts as the trial counts down:
 *   • > 10 days remaining  → blue (informational)
 *   • 4 – 10 days          → amber
 *   • 0 – 3 days           → red
 *   • trial ended          → red, "Choose a plan to continue" CTA
 */
export default function TrialBanner({ onGoToBilling }) {
  const [me, setMe] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { ok, data } = await apiRequest('GET', `${BACKEND_URL}/api/auth/me`);
        if (!cancelled && ok) setMe(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!me || me.subscription_status !== 'trialing') return null;
  if (!me.trial_ends_at) return null;

  const endsAt = new Date(me.trial_ends_at);
  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysLeft = Math.max(0, Math.ceil((endsAt - now) / msPerDay));
  const totalDays = 60;
  const elapsed = Math.min(totalDays, Math.max(0, totalDays - daysLeft));
  const pct = Math.round((elapsed / totalDays) * 100);

  let palette = {
    bg: 'linear-gradient(135deg, #579BFC, #0073EA)',
    border: 'transparent',
    text: '#ffffff',
    bar: '#ffffff',
    btnBg: '#ffffff',
    btnText: '#0073EA',
    boxShadow: '0 4px 16px rgba(0,115,234,0.2)',
  };

  const endsAtLabel = endsAt.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <div
      data-testid="trial-banner"
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        boxShadow: palette.boxShadow,
        borderRadius: 10,
        padding: '0.85rem 1.1rem',
        margin: 0,
        color: palette.text,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          {daysLeft > 0 ? (
            <>
              <strong data-testid="trial-banner-headline">
                Your free trial — {daysLeft} day{daysLeft === 1 ? '' : 's'} remaining of {totalDays}.
              </strong>
              <div style={{ fontSize: '0.85rem', marginTop: '0.2rem',
                            color: palette.text, opacity: 0.9 }}>
                Full Agency Pro access included. When trial ends on
                {' '}<strong>{endsAtLabel}</strong> choose a plan to continue.
              </div>
            </>
          ) : (
            <>
              <strong data-testid="trial-banner-headline">
                Your trial has ended.
              </strong>
              <div style={{ fontSize: '0.85rem', marginTop: '0.2rem',
                            color: palette.text, opacity: 0.9 }}>
                Choose a plan to continue using Estatel AI.
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onGoToBilling}
          data-testid="trial-banner-view-plans"
          style={{
            background: palette.btnBg, color: palette.btnText, border: 'none',
            padding: '0.5rem 1.1rem', borderRadius: 6,
            fontWeight: 600, fontSize: '0.88rem',
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          View Plans
        </button>
      </div>

      <div data-testid="trial-banner-progress"
           style={{ marginTop: '0.65rem', height: 8,
                    background: 'rgba(255,255,255,0.25)',
                    borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%',
                      background: palette.bar,
                      transition: 'width .3s ease' }} />
      </div>
    </div>
  );
}
